import { closingDocumentsAPI } from "./api";

/** Список подъездов «Подъезд N, Блок №…» — тот же API, что фильтр «Документы» в ResidentialSettings. */
export { getResidentialEntrances as getClosingDocumentEntrances } from "@/views/ResidentialSettings/services";

/**
 * Согласованный с тултипом «Подписанты» признак подписи для UI.
 * Если с бэка пришёл непустой assign_signs — считаем подписанным только когда все слоты is_signed;
 * иначе используем have_signed (частичная запись в sign_tab / шапка документа).
 */
export function closingDocumentsRowFullySigned(row) {
  if (!row) return false;
  const raw = row.assign_signs;
  if (Array.isArray(raw) && raw.length > 0) {
    return raw.every((it) => it.is_signed === true || it.is_signed === 1);
  }
  return !!(row.have_signed === true || row.have_signed === 1);
}

export const getResidents = async () => {
  try {
    return await closingDocumentsAPI.getResidents();
  } catch (e) {
    return null;
  }
};

/**
 * Типы проекта по ЖК (через существующий SP project_assign__read_project_type).
 */
export const getProjectTypes = async (resident_id) => {
  try {
    const { default: instance } = await import("@/services/api");
    const res = await instance().get(`/project/project_types/read/`, { params: { resident_id } });
    return res?.data;
  } catch (e) {
    return null;
  }
};

/**
 * Каскадные фильтры: контрагенты, блоки, этажи.
 * params: { resident_id, project_type_id, contractor_id, block_name, entrance }
 * Каждый следующий уровень сужает предыдущий.
 */
export const getCascadeFilters = async (params = {}) => {
  try {
    return await closingDocumentsAPI.getCascade(params);
  } catch (e) {
    return null;
  }
};

/**
 * Получить список закрывающих документов.
 * @param {Object} params - фильтры: project_id, doc_type, status, contractor_id, block, floor
 */
export const getClosingDocuments = async (params = {}) => {
  try {
    const res = await closingDocumentsAPI.getDocuments(params);
    return res;
  } catch (e) {
    return null;
  }
};

/**
 * Создать АВР из существующего КС2В (на бэке вместе с 3КС).
 * Использует SP: public.floor_map_document__create_followup_from_k2sv
 */
export const createFollowupFromK2sv = async (floor_map_document_id) => {
  try {
    const res = await closingDocumentsAPI.createFollowupFromK2sv(floor_map_document_id);
    return res;
  } catch (e) {
    throw e;
  }
};

export const uploadBuhAvrManualSign = async (payload) => {
  return closingDocumentsAPI.uploadBuhAvrManualSign(payload);
};

/**
 * Получить плоский список материалов для М19 перерасход (агрегат по всем проектам ЖК с типом).
 * Использует SP: public.material_overuse__read_wbs(..., optional sbs_sklad_id для «резерва»: М19 где is_file_sent_bi ещё false)
 */
export const getM19OverWbs = async (resident_id, project_type_id, sbs_sklad_id = undefined) => {
  try {
    const res = await closingDocumentsAPI.getM19OverWbs(resident_id, project_type_id, sbs_sklad_id);
    return res;
  } catch (e) {
    return null;
  }
};

export const getM19OverSkladOptions = async (project_id) => {
  try {
    return await closingDocumentsAPI.getM19OverSkladOptions(project_id);
  } catch (e) {
    return null;
  }
};

export const getM19PriceChanges = async (floor_map_document_id) => {
  try {
    const res = await closingDocumentsAPI.getM19PriceChanges(floor_map_document_id);
    return res?.data || null;
  } catch (e) {
    return null;
  }
};

export const previewM19OverDelete = async (floor_map_document_id) => {
  const res = await closingDocumentsAPI.previewM19OverDelete(floor_map_document_id);
  return res?.data || null;
};

export const deleteM19Over = async (material_overuse_id) => {
  return await closingDocumentsAPI.deleteM19Over(material_overuse_id);
};

export const previewM19OverWarehouseBalances = async (
  project_id,
  material_ids,
  sbs_sklad_id,
) => {
  try {
    return await closingDocumentsAPI.previewM19OverWarehouseBalances(
      project_id,
      material_ids,
      sbs_sklad_id,
    );
  } catch (e) {
    return null;
  }
};

/**
 * Создать документ М19 перерасход.
 * Использует SP: public.material_overuse__create_document
 */
export const createM19Over = async (project_id, materials, sbs_sklad_id) => {
  try {
    const res = await closingDocumentsAPI.createM19Over(project_id, materials, sbs_sklad_id);
    return res;
  } catch (e) {
    throw e;
  }
};

/**
 * Скачать предпросмотр М19 (PDF).
 */
export const downloadM19OverPreview = async (project_id, materials) => {
  try {
    const response = await closingDocumentsAPI.previewM19Over(project_id, materials);
    const { saveAs } = await import("file-saver");
    const blob = new Blob([response.data], { type: "application/pdf" });
    const filename = `M19_перерасход_${new Date().toLocaleDateString("ru")}.pdf`;
    saveAs(blob, filename);
    return true;
  } catch (e) {
    throw e;
  }
};

function _closingExcelQueryParams(row, fileTitle) {
  const params = { floor_map_document_id: row.floor_map_document_id };
  if (fileTitle) params.file_title = fileTitle;
  if (row.project_id != null) params.project_id = row.project_id;
  if (row.project_entrance_id != null) params.project_entrance_id = row.project_entrance_id;
  return params;
}

/** Скачать Excel (форма 2В / АВР 3‑2 / М‑19) для строки списка закрывающих документов. */
export const downloadClosingDocumentFormExcel = async (activeTabKey, row, fileTitle) => {
  const pathByTab = {
    k2sv: closingDocumentsAPI.downloadKc2bForm2vExcel,
    avr: closingDocumentsAPI.downloadAvr32Excel,
    m19: closingDocumentsAPI.downloadM19OverDocumentExcel,
  };
  const downloader = pathByTab[activeTabKey];
  if (!downloader || !row?.floor_map_document_id) {
    throw new Error("Некорректный тип документа или нет floor_map_document_id");
  }
  const res = await downloader(_closingExcelQueryParams(row, fileTitle));
  const { saveAs } = await import("file-saver");

  let filename = `${String(fileTitle || "document").replace(/[<>:"/\\|?*\x00-\x1f]/g, "_")}.xlsx`;
  const cd = res.headers?.["content-disposition"];
  if (cd) {
    const star = /filename\*=UTF-8''([^;\n]+)/i.exec(cd);
    const plain = /filename="([^"]+)"/i.exec(cd);
    try {
      if (star?.[1]) filename = decodeURIComponent(star[1].trim());
      else if (plain?.[1]) filename = plain[1].trim();
    } catch {
      /* оставить filename по умолчанию */
    }
  }
  const blob = new Blob([res.data], {
    type:
      res.headers["content-type"]
      || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, filename);
  return true;
};

/** Превью JSON: КС‑2В форма 2В (для уже созданного документа). */
export const getKc2bForm2vPreview = async (floor_map_document_id) => {
  try {
    const res = await closingDocumentsAPI.getKc2bForm2vPreview(floor_map_document_id);
    return res?.data ?? res;
  } catch {
    return null;
  }
};

/** Превью JSON: АВР 3‑2. */
export const getAvr32Preview = async (floor_map_document_id) => {
  try {
    const res = await closingDocumentsAPI.getAvr32Preview(floor_map_document_id);
    return res?.data ?? res;
  } catch {
    return null;
  }
};

/** Превью JSON: М‑19 перерасход по сохранённому документу. */
export const getM19OverDocumentPreview = async (floor_map_document_id) => {
  try {
    const res = await closingDocumentsAPI.getM19OverDocumentPreview(floor_map_document_id);
    return res?.data ?? res;
  } catch {
    return null;
  }
};

